import React, {
    useCallback,
    useState,
    useMemo,
    useEffect,
    useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Viewer from "react-viewer";
import { useCookies } from "react-cookie";
import { IChannel, IChoice, RootStateType } from "@Interfaces";
import {
    StyledChoiceGrid,
    StyledChoiceImage,
    StyledChoiceImageWrapper,
    StyledChoiceText,
    StyledChoiceValue,
    StyledChoiceWrapper,
} from "./Choice.styled";
import { ChoiceHTMLSelector } from "@Interfaces/Entities/Poll";
import Bar from "@Components/Bar/Bar.component";
import GetImagePath from "@Hooks/getImagePath";
import { PollActions } from "@Store";
import {
    IStoredGuestVote,
    persistGuestVotes,
    readGuestVotes,
    getPollIdsFromCookies,
} from "@Utils/guestVotes";
import {
    clearDoubleVoteMeta,
    DEFAULT_DOUBLE_VOTE_META,
    persistDoubleVoteMeta,
    readDoubleVoteMeta,
} from "@Utils/doubleVoteMeta";

type IChoiceComponentProps = {
    choice: IChoice;
    hasUserVoted: boolean;
    isExpiredDate: boolean;
    isGuest: boolean;
    isHideResult: boolean;
    pollId: string;
    totalVotes: number;
    userChoice: string | string[];
    isActive: boolean;
    allowDoubleVote: boolean;
    doubleVoteCooldownMinutes: number;
    onLocalGuestVote?: (selectedChoiceId: string) => void;
    onLocalGuestUnvote?: (selectedChoiceId: string) => void;
    onLocalGuestVoteChange?: (
        prevChoiceId: string,
        nextChoiceId: string
    ) => void;
    onUnvoteCountdownStart?: () => void;
    onUndoCountdownCancel?: () => void;
    onQueueVote?: (choiceId: string, deviceData: string) => void;
    onCancelQueuedVote?: () => void;
    sealedChoiceId?: string | null;
    sealedVoteCount?: number | null;
    canUndoVote: boolean;
    multiVoting?: boolean;
};

type ISessionVote = IStoredGuestVote;

const Choice = ({
    choice: { votes = 0, _id, txt, img },
    hasUserVoted,
    isExpiredDate,
    isGuest,
    isHideResult,
    pollId,
    totalVotes,
    userChoice,
    isActive,
    allowDoubleVote,
    doubleVoteCooldownMinutes,
    onLocalGuestVote,
    onLocalGuestUnvote,
    onLocalGuestVoteChange,
    onUnvoteCountdownStart,
    onUndoCountdownCancel,
    onQueueVote,
    onCancelQueuedVote,
    sealedChoiceId,
    sealedVoteCount,
    canUndoVote,
    multiVoting = false,
}: IChoiceComponentProps) => {
    const dispatch = useDispatch<any>();
    const channels: Array<IChannel> = useSelector(
        (state: RootStateType) => state.channels.channels
    );
    const channelOfCurrentPoll = useMemo(
        () =>
            channels.find((channel) =>
                channel.polls?.some((poll) => poll._id === pollId)
            ) || null,
        [channels, pollId]
    );
    const channelId = channelOfCurrentPoll?._id ?? null;
    const choiceImagePath = GetImagePath(img?.public_url, img?.fid);
    const [cookies, setCookie] = useCookies(["tb"]);
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [sessionVotes, setSessionVotes] = useState<ISessionVote[]>(() =>
        readGuestVotes()
    );
    const initialMeta = useMemo(
        () =>
            allowDoubleVote
                ? readDoubleVoteMeta(pollId)
                : DEFAULT_DOUBLE_VOTE_META,
        [allowDoubleVote, pollId]
    );
    const [doubleVoteConsumed, setDoubleVoteConsumed] = useState<boolean>(
        initialMeta.consumed
    );
    const [lastVoteAt, setLastVoteAt] = useState<number>(
        initialMeta.lastVoteAt
    );
    const [voteCount, setVoteCount] = useState<number>(initialMeta.voteCount);
    const [nowTs, setNowTs] = useState<number>(Date.now());
    const lastSealedRef = useRef<string | null>(null);

    useEffect(() => {
        // reload per-poll double-vote state when switching polls
        if (!allowDoubleVote) {
            setDoubleVoteConsumed(false);
            setLastVoteAt(0);
            setVoteCount(0);
            clearDoubleVoteMeta(pollId);
            setNowTs(Date.now());
            return;
        }

        const meta = readDoubleVoteMeta(pollId);
        setDoubleVoteConsumed(meta.consumed);
        setLastVoteAt(meta.lastVoteAt);
        setVoteCount(meta.voteCount);
        setNowTs(Date.now());
    }, [pollId, allowDoubleVote]);

    const doubleVoteCoolDownMs = useMemo(
        () =>
            allowDoubleVote
                ? Math.max(0, Number(doubleVoteCooldownMinutes) * 60 * 1000)
                : 0,
        [allowDoubleVote, doubleVoteCooldownMinutes]
    );

    const cooldownActive = useMemo(
        () =>
            allowDoubleVote &&
            voteCount === 1 &&
            !canUndoVote &&
            doubleVoteCoolDownMs > 0 &&
            nowTs - lastVoteAt < doubleVoteCoolDownMs,
        [
            allowDoubleVote,
            voteCount,
            canUndoVote,
            doubleVoteCoolDownMs,
            nowTs,
            lastVoteAt,
        ]
    );

    useEffect(() => {
        if (!cooldownActive) return;
        const id = setInterval(() => setNowTs(Date.now()), 1000);
        return () => clearInterval(id);
    }, [cooldownActive]);

    // Seal double-vote state once the queued vote is actually sent (parent notifies via sealedChoiceId/count).
    useEffect(() => {
        if (
            !sealedChoiceId ||
            sealedChoiceId !== _id ||
            lastSealedRef.current === sealedChoiceId ||
            !allowDoubleVote
        ) {
            return;
        }

        const now = Date.now();
        const nextCount =
            typeof sealedVoteCount === "number"
                ? Math.min(2, Math.max(1, sealedVoteCount))
                : Math.min(2, Math.max(1, voteCount + 1));
        const consumed = nextCount >= 2;

        setDoubleVoteConsumed(consumed);
        setLastVoteAt(now);
        setVoteCount(nextCount);

        persistDoubleVoteMeta(pollId, {
            consumed,
            voteCount: nextCount,
            lastVoteAt: now,
        });

        lastSealedRef.current = sealedChoiceId;
    }, [
        sealedChoiceId,
        sealedVoteCount,
        _id,
        allowDoubleVote,
        pollId,
        voteCount,
    ]);

    const guestVote = useMemo(
        () =>
            sessionVotes.find(
                (votedPoll: ISessionVote) => votedPoll.pollId === pollId
            ),
        [sessionVotes, pollId]
    );

    const hasGuestVoteInSession = useMemo(
        () => Boolean(guestVote?.choiceIds?.length),
        [guestVote]
    );

    const hasGuestVoteStoredInCookies = useMemo(() => {
        if (!isGuest) return false;

        const cookiePolls = getPollIdsFromCookies(cookies);
        return cookiePolls.includes(pollId);
    }, [cookies, isGuest, pollId]);

    const hasGuestVoted = hasGuestVoteStoredInCookies || hasGuestVoteInSession;

    const persistSessionVotes = useCallback((votes: ISessionVote[]) => {
        const sanitizedVotes = votes
            .map((vote) => ({
                pollId: vote.pollId,
                choiceIds: Array.from(
                    new Set(
                        vote.choiceIds.filter(
                            (choiceId) =>
                                typeof choiceId === "string" && choiceId
                        )
                    )
                ),
            }))
            .filter((vote) => vote.pollId && vote.choiceIds.length > 0);

        setSessionVotes(sanitizedVotes);
        persistGuestVotes(sanitizedVotes);
    }, []);

    const handleImageViewerOpen = useCallback(() => {
        setIsViewerOpen(true);
    }, []);

    // Compute if user has selected the choice
    const isUserChoice = useCallback(
        (choiceId: string) => {
            if (isGuest) {
                return guestVote?.choiceIds?.includes(choiceId) ?? false;
            }

            if (Array.isArray(userChoice)) {
                return userChoice.includes(choiceId);
            }

            return userChoice === choiceId;
        },
        [guestVote, isGuest, userChoice]
    );

    const closeImageViewer = (): void => {
        setIsViewerOpen(false);
    };

    /**
     * Computes this choice's vote share as an integer percent (0–100).
     * Safely handles invalid numbers and zero totals to avoid NaN/Infinity.
     * Memoized to only change when `votes` or `totalVotes` change.
     * @returns {number} Percentage value between 0 and 100.
     */
    const getPercent = useCallback(() => {
        const denom = Number(totalVotes);
        const num = Number(votes);
        if (!Number.isFinite(denom) || denom <= 0) return 0;
        if (!Number.isFinite(num) || num < 0) return 0;
        const pct = Math.round((num / denom) * 100);
        return Number.isFinite(pct) && !Number.isNaN(pct) ? pct : 0;
    }, [votes, totalVotes]);

    const removePollFromCookie = useCallback(
        (pollIds: string[]) => {
            if (pollIds.length > 0) {
                setCookie("tb", pollIds.join(";"), { path: "/" });
            } else {
                setCookie("tb", "", { path: "/" });
            }
        },
        [setCookie]
    );

    const handleGuestUnvote = useCallback(() => {
        if (!guestVote || !canUndoVote || multiVoting) {
            return;
        }

        if (onCancelQueuedVote) {
            onCancelQueuedVote();
        }
        const existingPolls = getPollIdsFromCookies(cookies);
        const updatedPolls = existingPolls.filter(
            (id: string) => id !== pollId
        );
        removePollFromCookie(updatedPolls);

        const updatedSessionVotes = sessionVotes.filter(
            (vote) => vote.pollId !== pollId
        );
        persistSessionVotes(updatedSessionVotes);

        // Rolling back a pending vote should not start the double-vote cooldown.
        if (allowDoubleVote) {
            const meta = readDoubleVoteMeta(pollId);
            setDoubleVoteConsumed(meta.consumed);
            setLastVoteAt(meta.lastVoteAt);
            setVoteCount(meta.voteCount);
            setNowTs(Date.now());
        }

        if (onLocalGuestUnvote && guestVote.choiceIds?.[0]) {
            onLocalGuestUnvote(guestVote.choiceIds[0]);
        }

        if (onUndoCountdownCancel) {
            onUndoCountdownCancel();
        }

        if (!onCancelQueuedVote && channelId) {
            dispatch(PollActions.deleteGuestVote(pollId, channelId));
        }
    }, [
        guestVote,
        canUndoVote,
        multiVoting,
        onCancelQueuedVote,
        cookies,
        removePollFromCookie,
        sessionVotes,
        persistSessionVotes,
        onLocalGuestUnvote,
        onUndoCountdownCancel,
        pollId,
        channelId,
        dispatch,
        allowDoubleVote,
    ]);

    const handleGuestVoteChange = useCallback(
        (choiceId: string) => {
            if (multiVoting) {
                return;
            }
            // Only allow change within the undo window.
            if (!canUndoVote) {
                return;
            }

            const previousChoiceId = guestVote?.choiceIds?.[0];

            const nextVotes = sessionVotes.map((vote) =>
                vote.pollId === pollId
                    ? { pollId, choiceIds: [choiceId] }
                    : vote
            );

            persistSessionVotes(nextVotes);

            if (onUnvoteCountdownStart) {
                onUnvoteCountdownStart();
            }

            if (onLocalGuestVoteChange && previousChoiceId) {
                onLocalGuestVoteChange(previousChoiceId, choiceId);
            }

            if (onQueueVote) {
                const DeviceDetector = require("device-detector-js");
                const deviceData = JSON.stringify(
                    new DeviceDetector().parse(navigator.userAgent)
                );
                onQueueVote(choiceId, deviceData);
            } else if (channelId) {
                dispatch(
                    PollActions.replaceGuestVote(pollId, channelId, choiceId)
                );
            }
        },
        [
            persistSessionVotes,
            pollId,
            sessionVotes,
            onUnvoteCountdownStart,
            onLocalGuestVoteChange,
            onQueueVote,
            canUndoVote,
            multiVoting,
            guestVote,
            channelId,
            dispatch,
        ]
    );

    const registerGuestVote = useCallback(
        (choiceId: string, shouldAllowUndo: boolean) => {
            // Fresh vote inside the undo window: do not mark consumed until the request is sealed.
            if (!shouldAllowUndo) {
                setDoubleVoteConsumed(false);
            }
            const now = Date.now();
            const nextVoteCount = allowDoubleVote
                ? Math.min(2, (voteCount || 0) + 1)
                : 1;
            const consumed =
                !shouldAllowUndo && allowDoubleVote && nextVoteCount >= 2;

            setVoteCount(nextVoteCount);
            setLastVoteAt(now);
            if (!shouldAllowUndo) {
                setDoubleVoteConsumed(consumed);
            }

            if (allowDoubleVote && !shouldAllowUndo) {
                persistDoubleVoteMeta(pollId, {
                    consumed,
                    voteCount: nextVoteCount,
                    lastVoteAt: now,
                });
            }

            let updatedPolls = getPollIdsFromCookies(cookies);
            if (!updatedPolls.includes(pollId)) {
                updatedPolls = [...updatedPolls, pollId];
                setCookie("tb", updatedPolls.join(";"), { path: "/" });
            }

            const existingEntry = sessionVotes.find(
                (vote) => vote.pollId === pollId
            );

            const nextSessionVotes = existingEntry
                ? sessionVotes.map((vote) =>
                      vote.pollId === pollId
                          ? {
                                pollId,
                                // For single-choice polls, always store only the latest choice.
                                choiceIds: multiVoting
                                    ? Array.from(
                                          new Set([...vote.choiceIds, choiceId])
                                      )
                                    : [choiceId],
                            }
                          : vote
                  )
                : [...sessionVotes, { pollId, choiceIds: [choiceId] }];

            persistSessionVotes(nextSessionVotes);

            if (onLocalGuestVote) {
                onLocalGuestVote(choiceId);
            }
            if (shouldAllowUndo && onUnvoteCountdownStart) {
                onUnvoteCountdownStart();
            }

            const DeviceDetector = require("device-detector-js");
            const deviceData = JSON.stringify(
                new DeviceDetector().parse(navigator.userAgent)
            );

            if (onQueueVote) {
                onQueueVote(choiceId, deviceData);
            } else if (channelId) {
                dispatch(
                    PollActions.submitVote(pollId, channelId, txt, {
                        choice_id: choiceId,
                        device: deviceData,
                    })
                );
            }
        },
        [
            allowDoubleVote,
            voteCount,
            cookies,
            pollId,
            sessionVotes,
            persistSessionVotes,
            onLocalGuestVote,
            onUnvoteCountdownStart,
            onQueueVote,
            setCookie,
            multiVoting,
            channelId,
            dispatch,
            txt,
        ]
    );

    const handleVote = (choiceId: string) => {
        // Disallow interactions when poll is inactive/expired
        if (!isActive || isExpiredDate) return;
        // Only guests can vote on polls
        if (!isGuest) return;

        const alreadySelected = guestVote?.choiceIds?.includes(choiceId);

        if (cooldownActive) {
            return;
        }

        if (multiVoting) {
            if (alreadySelected) {
                return;
            }
            registerGuestVote(choiceId, false);
            return;
        }

        if (doubleVoteConsumed) {
            return;
        }

        if (guestVote) {
            // Within undo window: allow undo/change; otherwise allow the one-time double vote if enabled and cooldown passed.
            if (canUndoVote) {
                if (guestVote.choiceIds?.[0] === choiceId) {
                    handleGuestUnvote();
                } else {
                    handleGuestVoteChange(choiceId);
                }
                return;
            }

            if (
                allowDoubleVote &&
                !doubleVoteConsumed &&
                !cooldownActive &&
                // Avoid sending a redundant double vote on the same choice; backend rejects with vote_choice_unchanged.
                !alreadySelected
            ) {
                registerGuestVote(choiceId, true);
            }
            return;
        }
        // Check if the guest has already voted via cookies
        if (hasGuestVoted && !allowDoubleVote && !canUndoVote) return;

        registerGuestVote(choiceId, true);
    };

    // Authenticated users (admins/authors) should always see results.
    // Guests never see results before voting, and never see them at all
    // (bar or %) while the channel's hide-results setting is enabled.
    const shouldHideResultVisuals =
        !isExpiredDate && isGuest && (!hasGuestVoted || isHideResult);

    const isVoteDisabled =
        isExpiredDate ||
        (!multiVoting && hasUserVoted && !allowDoubleVote && !canUndoVote) ||
        doubleVoteConsumed ||
        cooldownActive ||
        !isGuest;

    const shouldShowResultVisuals = !shouldHideResultVisuals;
    // Once the vote request has gone through, keep the selected choice visually
    // prominent (full fill) even though the real percentage stays hidden.
    const shouldShowSelectedFill =
        !shouldShowResultVisuals && hasGuestVoted && isUserChoice(_id);
    return (
        <>
            <StyledChoiceWrapper
                style={{ zIndex: isViewerOpen ? 1 : 0 }}
                isUserChoice={isUserChoice(_id)}
                hoverEffect={
                    !isExpiredDate &&
                    !(hasGuestVoted && !multiVoting) &&
                    !(hasUserVoted && !multiVoting)
                }
            >
                {img && (
                    <StyledChoiceImageWrapper
                        onClick={() => handleImageViewerOpen()}
                    >
                        <StyledChoiceImage src={`${choiceImagePath}`} />
                    </StyledChoiceImageWrapper>
                )}
                <StyledChoiceGrid
                    onClick={() => handleVote(_id)}
                    id={`${ChoiceHTMLSelector.id}${_id}`}
                    centeredChild={!shouldShowResultVisuals}
                    isDisabled={isVoteDisabled}
                >
                    <StyledChoiceText
                        id={`${ChoiceHTMLSelector.txt}${_id}`}
                        textPadding={
                            shouldShowResultVisuals ||
                            isExpiredDate ||
                            hasUserVoted
                        }
                    >
                        {txt}
                    </StyledChoiceText>
                    {shouldShowResultVisuals && (
                        <>
                            <Bar
                                percent={getPercent()}
                                isUserChoice={isUserChoice(_id)}
                            />

                            <StyledChoiceValue>{`${getPercent()}%`}</StyledChoiceValue>
                        </>
                    )}
                    {shouldShowSelectedFill && (
                        <Bar percent={100} isUserChoice />
                    )}
                </StyledChoiceGrid>
            </StyledChoiceWrapper>

            <Viewer
                visible={isViewerOpen}
                onClose={closeImageViewer}
                images={[{ src: choiceImagePath, alt: txt }]}
                noNavbar={false}
                noImgDetails={true}
                changeable={false}
                defaultSize={{ width: 400, height: 400 }}
                showTotal={false}
                onMaskClick={closeImageViewer}
                disableMouseZoom={true}
            />
        </>
    );
};

export default React.memo(Choice);
